import { fallbackLng } from '@heraion/i18n';
import { DAY_MS } from '@heraion/shared';

// Locale-aware short-date formatting. Hermes ships full Intl on RN
// 0.85 (Android), so DateTimeFormat is available across every API
// level we target. The Intl.DateTimeFormat constructor is non-trivial
// to instantiate (loads ICU data + resolves the format), so we cache
// per-locale and reuse.

const cache = new Map<string, Intl.DateTimeFormat>();

const buildFormatter = (locale: string): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return new Intl.DateTimeFormat(fallbackLng, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
};

const getFormatter = (locale: string): Intl.DateTimeFormat => {
  let f = cache.get(locale);
  if (!f) {
    f = buildFormatter(locale);
    cache.set(locale, f);
  }
  return f;
};

export function formatShortDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return getFormatter(locale).format(d);
}

// The day with its month as a word and the year ("02 Sep 2026", "02 set
// 2026"): the shape the Logger's date field, the Plan date field and the
// Progress report show. Moved here from DateEditorCard (PG-64) so the three
// share one copy. Same Hermes fallback: a locale Intl cannot build falls back
// to the platform's own date string.
export function formatDayMonthYear(d: Date, locale: string): string {
  if (Number.isNaN(d.getTime())) return '–';
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

// Day and short month ("18 Aug", "18 ago"). The year only past 300 days,
// where the month alone could read as this year's. Same Hermes fallback.
// The chart panels that format day and month inline can move here.
const YEAR_SHOWN_AFTER_DAYS = 300;
export function formatDayMonth(d: Date, locale: string, nowMs = Date.now()): string {
  if (Number.isNaN(d.getTime())) return '–';
  const withYear = nowMs - d.getTime() > YEAR_SHOWN_AFTER_DAYS * DAY_MS;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' as const } : {}),
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

// Day, short month and time ("2 Oct, 14:35", "2 ott, 14:35"), for readings
// that share a day (glucose). One formatter per locale, a chart formats
// hundreds of points. Same Hermes fallback.
const dayMonthTimeCache = new Map<string, Intl.DateTimeFormat | null>();
export function formatDayMonthTime(d: Date, locale: string): string {
  if (Number.isNaN(d.getTime())) return '–';
  if (!dayMonthTimeCache.has(locale)) {
    try {
      dayMonthTimeCache.set(
        locale,
        new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }),
      );
    } catch {
      dayMonthTimeCache.set(locale, null);
    }
  }
  return dayMonthTimeCache.get(locale)?.format(d) ?? d.toLocaleString();
}
